import type {
  ChatContextProps,
  FilterAnalyticsProps,
  ParticipantOpenedProps,
  PriorityWireValue,
} from 'src/participantsHome/participantListAnalytics'

/**
 * The participant list publishes its current triage context here so events fired from
 * elsewhere in the dashboard (chat_send, mark_as_unread, participant_opened) can carry the
 * same properties without threading filter state through half the app.
 *
 * It is published *only* while the new triage UI is shown
 * (`featureFlagDashboardPriorityFilters`), so a `null` snapshot means "old UI" — which is
 * what gates those events.
 */
export type ParticipantListSnapshot = {
  filterProps: FilterAnalyticsProps
  chatSendProps: ChatContextProps
  /** For the participant the list has selected, when it has one. */
  participantOpened?: { uid: string; props: ParticipantOpenedProps }
}

/** The bands the last `participant_opened` went out with, for the participant it was about. */
export type ParticipantOpenedBands = { uid: string; bands: Array<PriorityWireValue> }

let current: ParticipantListSnapshot | null = null
let lastOpened: ParticipantOpenedBands | null = null
const listeners = new Set<() => void>()

export const setParticipantListSnapshot = (snapshot: ParticipantListSnapshot | null) => {
  current = snapshot
  listeners.forEach((listener) => listener())
}

export const getParticipantListSnapshot = (): ParticipantListSnapshot | null => current

/** Calls `listener` on every publish. Returns the unsubscribe. */
export const subscribeToParticipantListSnapshot = (listener: () => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * `participant_opened` props for `uid`, or null when the list is on the old UI or does not
 * have that participant selected — e.g. a deep link to someone outside the home view.
 */
export const getParticipantOpenedProps = (uid: string): ParticipantOpenedProps | null => {
  const opened = current?.participantOpened
  return opened && opened.uid === uid ? opened.props : null
}

export const setParticipantOpenedBands = (opened: ParticipantOpenedBands | null) => {
  lastOpened = opened
}

/**
 * `chat_send` props, or undefined on the old UI. Filter props are live, but the bands are the
 * ones the participant was opened with: marking the feed seen clears the unread indicator (and
 * with it the Waiting band) before the therapist has replied, so the live bands would no longer
 * say why the participant was opened. Falls back to the live bands when none were captured for
 * the selected participant.
 */
export const getChatSendProps = (): ChatContextProps | undefined => {
  if (!current) {
    return undefined
  }
  const { chatSendProps, participantOpened } = current
  return lastOpened && participantOpened?.uid === lastOpened.uid
    ? // eslint-disable-next-line camelcase
      { ...chatSendProps, patient_priority_bands: lastOpened.bands }
    : chatSendProps
}
